import {
  Box,
  Button,
  DialogContent,
  DialogContentText,
  useTheme,
} from "@mui/material";
import React, { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import InfoIcon from "@icons/myicons/warning.svg?react";
import { LoadingButton } from "../../common/progress/LoadingButton";
import { Modal } from "../Modal";
import { Banner } from "../../Banner";

export interface ConfirmDeleteDialogProps {
  isOpen: boolean;
  title: string;
  errorMessage?: string;
  onClose: () => void;
  onDelete: () => void;
  isDeleteRequestLoading?: boolean;
  text?: string;
  confirmButtonText?: string;
  cancelButtonText?: string;
  children?: ReactNode;
  testId?: string;
}

export const ConfirmDeleteDialog = (props: ConfirmDeleteDialogProps) => {
  const {
    isOpen,
    title,
    testId,
    text,
    confirmButtonText,
    cancelButtonText,
    errorMessage,
    onClose,
    onDelete,
    children,
    isDeleteRequestLoading = false,
  } = props;
  const { t } = useTranslation();
  const theme = useTheme();

  const noButtonText = cancelButtonText || t("navigation.deleteModal.no");

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      height="auto"
      testId={testId}
      width={680}
    >
      <Box mb={11} sx={{ textAlign: "left" }}>
        {errorMessage && (
          <Banner
            type="error"
            icon={<InfoIcon color={theme.palette.error.dark} />}
            data-testid="delete-error-alert"
            text={errorMessage}
            mb={4}
          />
        )}
        <DialogContentText>
          {text || t("navigation.deleteModal.cannotBeUndone")}
        </DialogContentText>
        {children && (
          <DialogContent sx={{ padding: 0 }}>{children}</DialogContent>
        )}
      </Box>
      <Box display="flex" justifyContent="flex-end" gap={2}>
        <Button variant="outlined" onClick={onClose}>
          {noButtonText}
        </Button>
        <LoadingButton
          data-testid="delete-modal-delete-action-button"
          variant="primary"
          color="error"
          autoFocus
          onClick={onDelete}
          disabled={isDeleteRequestLoading}
          isFetching={isDeleteRequestLoading}
        >
          {confirmButtonText || t("navigation.deleteModal.yes")}
        </LoadingButton>
      </Box>
    </Modal>
  );
};
